import React, {useState} from 'react';
import {Form, RadioButton} from '@deere/ux.uxframe-react';
import GetAppIcon from '@material-ui/icons/GetApp';
import IconButton from '@mui/material/IconButton';
import styles from './index.css';
import {SpinnerIcon} from '../../Icon';
import {DeereFetch} from '../../../shared/fetch';
import {genericErrorMessage, noSignedDocumentsAvailable} from '../Constants/constants';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {DocumentDownloadDropdown} from './DocumentDownloadDropdown';

export default function DownloadSignedDocuments(props) {
    const intl = useIntl();
    const [documentType, setDocumentType] = useState('COMBINED');
    const [downloading, setDownloading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const {
        bundleNumber,
        documentsInEnvelope,
        envelopeId,
        envelopeStatus,
        icsID,
        quoteNumber,
        userBranchCode,
        userHasAccessToERegistry
    } = props;
    const disableDocumentDownload = !(envelopeStatus === 'completed' && userBranchCode.includes('DRO', 'ASM'));

    const documentTypeMap = {
        'COMBINED': {
            documentName: 'combined.pdf',
            downloadFileName: `${bundleNumber}-${quoteNumber}.pdf`
        },
        'SEPARATE': {
            documentName: 'archive.zip',
            downloadFileName: `${bundleNumber}-${quoteNumber}.zip`
        },
        'E-REGISTRY_DOWNLOAD': {
            documentName: 'ERegistry.zip',
            downloadFileName: `${bundleNumber}-${quoteNumber}-${icsID}.zip`
        }
    }

    const downloadSignedDocuments = async () => {
        setDownloading(true);
        setErrorMessage('');
        try {
            const documentName = documentTypeMap[documentType].documentName;
            const signedDocumentResponse = await DeereFetch(`/api/esign/envelope/${envelopeId}/${documentName}`);
            const documentContent = 'data:application/pdf;base64,' + signedDocumentResponse.documentData;
            if (signedDocumentResponse.documentData) {
                const documentLink = document.createElement('a');
                document.body.appendChild(documentLink);
                documentLink.href = documentContent;
                documentLink.download = documentTypeMap[documentType].downloadFileName;
                documentLink.click();
                documentLink.remove();
            } else {
                setErrorMessage(intl.formatMessage({id: noSignedDocumentsAvailable}));
            }
        } catch (err) {
            setErrorMessage(intl.formatMessage({id: genericErrorMessage}));
        } finally {
            setDownloading(false);
        }
    };

    function radioButtonHandler(value) {
        setDocumentType(value);
    }

    function dropdownHandler(value) {
        setDocumentType(value);
    }

    const componentMap = {
        [userHasAccessToERegistry]: (
            <DocumentDownloadDropdown
                disabled={disableDocumentDownload}
                documentType={documentType}
                documentsInEnvelope={documentsInEnvelope}
                dropdownHandler={dropdownHandler}
                envelopeId={envelopeId}
            />
        ),
        [!userHasAccessToERegistry]: (
            <>
                <RadioButton
                    checked={documentType === 'COMBINED'}
                    className={styles.downloadDocRadio}
                    disabled={disableDocumentDownload}
                    id={`pdf-${envelopeId}`}
                    inline={true}
                    label={<FormattedMessage id={'labels.COMBINED'}/>}
                    name={envelopeId}
                    onChange={() => radioButtonHandler('COMBINED')}
                    value={'COMBINED'}
                />
                <RadioButton
                    checked={documentType === 'SEPARATE'}
                    className={styles.downloadDocRadio}
                    disabled={disableDocumentDownload}
                    id={`zip-${envelopeId}`}
                    inline={true}
                    label={<FormattedMessage id={'labels.SEPARATE'}/>}
                    name={envelopeId}
                    onChange={() => radioButtonHandler('SEPARATE')}
                    value={'SEPARATE'}
                />
            </>
        )
    }

    return (
        <Form.Group className={styles.downloadSignedDocDiv}>
            <fieldset>
                <p className="download-docs">
                    <FormattedMessage id={'labels.DOWNLOAD'}/>
                </p>
                <span>
                    {componentMap.true}
                    <div className={styles.downloadFiles}>
                        {downloading && <SpinnerIcon size="sm"/>}
                        {!downloading && (
                            <IconButton
                                className={styles.downloadIcon}
                                disabled={disableDocumentDownload}
                                onClick={downloadSignedDocuments}
                                size="large"
                                title={intl.formatMessage({id: 'labels.DOWNLOAD'})}
                            >
                                {<GetAppIcon/>}
                            </IconButton>)
                        }
                    </div>
                </span>
            </fieldset>
            {errorMessage && <span className={styles.downloadError}>{errorMessage}</span>}
        </Form.Group>
    )
}
DownloadSignedDocuments.propTypes = {
    bundleNumber: PropTypes.string.isRequired,
    documentsInEnvelope: PropTypes.array.isRequired,
    envelopeId: PropTypes.string.isRequired,
    envelopeStatus: PropTypes.string.isRequired,
    icsID: PropTypes.string,
    quoteNumber: PropTypes.string.isRequired,
    userBranchCode: PropTypes.string.isRequired,
    userHasAccessToERegistry: PropTypes.bool.isRequired
}
